import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ApiError, api } from "@/api/client";
import type { PublicShareItemDetail, PublicShareItemSummary, PublicShareLink } from "@/types";
import {
  AgentCard,
  BenchmarkBadge,
  LangSwitcher,
  PublicShell,
  SourceLinkBadge,
  WhatsAppIcon,
  BUILDING_STATUS_LABEL_BY_LANG,
  CONDITION_LABEL_BY_LANG,
  DEAL_TYPE_LABEL_BY_LANG,
  PROPERTY_TYPE_LABEL_BY_LANG,
  formatPrice,
  pickDisplayPrice,
  normalizeCurrency,
  type DisplayCurrency,
} from "@/pages/ClientShareView";
import {
  DEFAULT_LANG,
  dealTypeLabel,
  featureLabel,
  type ShareLang,
  listingHeadline,
  meridianFontVars,
  normalizeLang,
  propertyTypeLabel,
  t,
} from "@/i18n/share";

const langStorageKey = (token: string) => `agencygrid:share-lang:${token}`;
const currencyStorageKey = (token: string) =>
  `agencygrid:share-currency:${token}`;

/** Per-property sales page. Three-section layout:
 *
 *   1. Cinematic hero gallery (full-bleed, lightbox-on-click).
 *   2. Two-column body — facts/description on the left, sticky agent CTA
 *      on the right. The agent card is the conversion target; we pin it so
 *      "Call now" / "WhatsApp" stays visible no matter how far the buyer
 *      scrolls through the description.
 *   3. Final CTA strip — gives the buyer a second, full-width chance to
 *      reach out without scrolling back up.
 *
 *  Language: same convention as the grid view — link's stored language
 *  wins by default, buyer override persists per-token in localStorage.
 */
export function ClientShareItemView() {
  const { token, itemId } = useParams<{ token: string; itemId: string }>();
  const [item, setItem] = useState<PublicShareItemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [langOverride, setLangOverride] = useState<ShareLang | null>(() => {
    if (!token || typeof window === "undefined") return null;
    const v = window.localStorage.getItem(langStorageKey(token));
    return v ? normalizeLang(v) : null;
  });
  // Buyer can switch language right here on the detail page too (not just on
  // the grid). Persist to the shared per-token key so the choice sticks when
  // they navigate back to the grid or into a sibling listing.
  const setLang = useCallback(
    (next: ShareLang) => {
      setLangOverride(next);
      if (token && typeof window !== "undefined") {
        window.localStorage.setItem(langStorageKey(token), next);
      }
    },
    [token],
  );
  // Currency mode mirrors the grid view's setting via the shared
  // localStorage key — flipping the toggle on either page sticks.
  const [currency, setCurrencyState] = useState<DisplayCurrency>(() => {
    if (!token || typeof window === "undefined") return "native";
    return normalizeCurrency(window.localStorage.getItem(currencyStorageKey(token)));
  });
  const setCurrency = useCallback(
    (next: DisplayCurrency) => {
      setCurrencyState(next);
      if (token && typeof window !== "undefined") {
        window.localStorage.setItem(currencyStorageKey(token), next);
      }
    },
    [token],
  );

  const lang: ShareLang = langOverride ?? normalizeLang(item?.language ?? null);

  const load = useCallback(async () => {
    if (!token || !itemId) return;
    try {
      const r = await api<PublicShareItemDetail>(
        `/api/public/share/${token}/items/${itemId}`,
      );
      setItem(r);
      setError(null);
    } catch (err) {
      const fallbackLang = langOverride ?? DEFAULT_LANG;
      if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
        setError(t("err_listing_gone", fallbackLang));
      } else {
        setError(err instanceof Error ? err.message : t("err_failed_load", fallbackLang));
      }
    }
  }, [token, itemId, langOverride]);

  // Sibling offers in the same share — used for the "browse the rest"
  // section at the bottom so the buyer doesn't have to bounce back to the
  // grid view to see what else the agent picked. Failure here is silent on
  // purpose: if the share fetch fails we just hide the section, the main
  // detail still renders.
  const [otherItems, setOtherItems] = useState<PublicShareItemSummary[]>([]);
  const loadShare = useCallback(async () => {
    if (!token || !itemId) return;
    try {
      const r = await api<PublicShareLink>(`/api/public/share/${token}`);
      setOtherItems(
        r.items.filter((i) => i.status === "ready" && i.id !== itemId),
      );
    } catch {
      setOtherItems([]);
    }
  }, [token, itemId]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-fetch siblings when the URL switches between detail pages — same
  // token but different ``itemId`` should refresh the "others" list so the
  // currently-viewed card disappears from it.
  useEffect(() => {
    loadShare();
  }, [loadShare]);

  // Scroll to top when the buyer hops between sibling listings (e.g. taps
  // a card in the "Other offers" grid). Without this, react-router
  // preserves scroll position so they land halfway down the new page,
  // staring at the *new* "other offers" section instead of the hero photo
  // they just clicked. Smooth scroll feels more intentional than the
  // hard jump — they see the page rewind, then the new hero settles in.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [itemId]);

  useEffect(() => {
    if (!item || item.status !== "preparing") return;
    const id = window.setInterval(load, 3000);
    return () => window.clearInterval(id);
  }, [item, load]);

  // Lightbox keyboard nav.
  useEffect(() => {
    if (!lightboxOpen || !item) return;
    const total = Math.max(item.photos.length, 1);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowRight") setActivePhoto((i) => (i + 1) % total);
      if (e.key === "ArrowLeft") setActivePhoto((i) => (i - 1 + total) % total);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, item]);

  const facts = useMemo(() => {
    if (!item) return [];
    const out: Array<{ label: string; value: string; icon: React.ReactNode }> = [];
    if (item.rooms != null)
      out.push({ label: t("fact_rooms", lang), value: String(item.rooms), icon: <IconRooms /> });
    // Bedrooms is the buyer-facing detail (vs ``rooms`` which is total
    // including living rooms etc.) — surface it whenever populated.
    if (item.bedrooms != null && item.bedrooms > 0)
      out.push({
        label: t("fact_bedrooms", lang),
        value: String(item.bedrooms),
        icon: <IconBedrooms />,
      });
    if (item.bathrooms != null && item.bathrooms > 0)
      out.push({
        label: t("fact_bathrooms", lang),
        value: String(item.bathrooms),
        icon: <IconBathrooms />,
      });
    if (item.area_sqm)
      out.push({
        label: t("fact_area", lang),
        value: `${Math.round(Number(item.area_sqm))} m²`,
        icon: <IconArea />,
      });
    if (item.floor != null)
      out.push({
        label: t("fact_floor", lang),
        value: item.total_floors ? `${item.floor} / ${item.total_floors}` : String(item.floor),
        icon: <IconFloor />,
      });
    if (item.property_type)
      out.push({
        label: t("fact_type", lang),
        value: propertyTypeLabel(item.property_type, lang) || item.property_type,
        icon: <IconType />,
      });
    if (item.building_status)
      out.push({
        label: t("fact_building", lang),
        value: BUILDING_STATUS_LABEL_BY_LANG[lang][item.building_status] || item.building_status,
        icon: <IconBuilding />,
      });
    if (item.condition)
      out.push({
        label: t("fact_condition", lang),
        value: CONDITION_LABEL_BY_LANG[lang][item.condition] || item.condition,
        icon: <IconCondition />,
      });
    if (item.year_built != null && item.year_built > 0)
      out.push({
        label: t("fact_year_built", lang),
        value: String(item.year_built),
        icon: <IconYear />,
      });
    if (item.street)
      out.push({
        label: t("fact_street", lang),
        value: item.street,
        icon: <IconStreet />,
      });
    return out;
  }, [item, lang]);

  if (error) return <PublicShell title={t("err_listing_unavailable", lang)}>{error}</PublicShell>;
  if (!item)
    return (
      <PublicShell title={t("loading_title", lang)}>{t("loading_detail_blurb", lang)}</PublicShell>
    );

  const photos = item.photos;
  const cover = photos[activePhoto] || photos[0] || null;
  const dealLabel = dealTypeLabel(item.deal_type, lang);
  const titleForMessages = item.title || t("fallback_listing_label", lang);

  return (
    <div
      className="mshare min-h-screen bg-[var(--mrd-bg)] text-[var(--mrd-ink)]"
      data-lang={lang}
      style={meridianFontVars(lang)}
    >
      {/* Slim sticky header — back link + agent CTA always reachable. */}
      <header className="sticky top-0 z-30 border-b border-[var(--mrd-line)] bg-[rgba(255,255,255,.92)] backdrop-blur-[14px] shadow-[0_1px_16px_rgba(18,32,54,.06)] safe-pt">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 md:px-8 h-[68px] flex items-center justify-between gap-2 sm:gap-3">
          <Link
            to={`/share/${token}`}
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[.14em] text-[var(--mrd-muted)] hover:text-[var(--mrd-navy)] transition-colors min-w-0"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <path d="m15 18-6-6 6-6" />
            </svg>
            <span className="truncate">{t("back_to_selection", lang)}</span>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {/* Currency picker — same shape as on the grid view; the
                shared localStorage key keeps the two pages in sync. */}
            <div
              role="radiogroup"
              aria-label={t("currency_picker_label", lang)}
              className="inline-flex items-center gap-1"
            >
              {([
                { code: "native" as const, label: t("currency_native", lang), short: "₾" },
                { code: "usd" as const, label: t("currency_usd", lang), short: "$" },
              ]).map(({ code, label, short }) => {
                const active = code === currency;
                return (
                  <button
                    key={code}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    title={label}
                    onClick={() => setCurrency(code)}
                    className={`font-mono rounded-[999px] px-2.5 py-1.5 text-[10px] uppercase tracking-[.05em] leading-none transition-colors ${
                      active
                        ? "bg-[var(--mrd-accent)] text-[var(--mrd-ink)] font-semibold"
                        : "text-[var(--mrd-navy)] opacity-70 hover:opacity-100"
                    }`}
                  >
                    <span className="md:hidden" aria-hidden>{short}</span>
                    <span className="hidden md:inline">{label}</span>
                    <span className="sr-only">{label}</span>
                  </button>
                );
              })}
            </div>
            <LangSwitcher lang={lang} onChange={setLang} />
            {item.agent.phone && (
              <a
                href={`tel:${item.agent.phone}`}
                className="inline-flex items-center gap-1.5 rounded-[999px] bg-[var(--mrd-accent)] hover:bg-[var(--mrd-accent-hover)] text-[var(--mrd-ink)] text-xs font-semibold px-4 py-2 transition-colors"
                aria-label={t("btn_call", lang)}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
                </svg>
                <span className="hidden lg:inline">{item.agent.phone}</span>
                <span className="hidden sm:inline lg:hidden">{t("btn_call", lang)}</span>
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8 pb-28 sm:pb-20 space-y-8 sm:space-y-10">
        {/* ─── Hero gallery ───────────────────────────────────────── */}
        <section className="space-y-3 pt-6">
          <div
            className="group relative rounded-2xl overflow-hidden bg-[var(--mrd-bg)] border border-[var(--mrd-line)] cursor-zoom-in shadow-[0_12px_40px_rgba(18,32,54,.12)] aspect-[4/3] sm:aspect-[16/10] md:aspect-[16/9] lg:aspect-auto lg:h-[clamp(440px,68vh,720px)]"
            onClick={() => cover && setLightboxOpen(true)}
          >
            {cover ? (
              <img
                key={cover}
                src={cover}
                alt={item.title || ""}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02] animate-[fadeIn_.4s_ease-out]"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[var(--mrd-muted)] text-sm">
                {item.status === "preparing"
                  ? t("preparing_photos", lang)
                  : t("no_photos_available", lang)}
              </div>
            )}

            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePhoto((i) => (i - 1 + photos.length) % photos.length);
                  }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 backdrop-blur-md text-white flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                  aria-label={t("btn_prev", lang)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePhoto((i) => (i + 1) % photos.length);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 backdrop-blur-md text-white flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                  aria-label={t("btn_next", lang)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
                <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-md border border-white/10 px-3 py-1 text-[11px] font-semibold text-white tabular-nums">
                  {t("counter_x_of_y", lang, { index: activePhoto + 1, total: photos.length })}
                </div>
              </>
            )}

            {dealLabel && (
              <span className="absolute top-4 left-4 inline-flex items-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1 text-[11px] font-mono uppercase tracking-[.14em] text-white">
                {dealLabel}
              </span>
            )}
          </div>

          {photos.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
              {photos.map((p, i) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setActivePhoto(i)}
                  className={`flex-shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 transition ${
                    i === activePhoto
                      ? "border-[var(--mrd-navy)]"
                      : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={p} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </section>

        {/* ─── Headline + body ────────────────────────────────────── */}
        <section className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 lg:gap-10 items-start">
          <div className="space-y-8 min-w-0">
            {/* Staff-only provenance row. ``source_url`` is withheld by the
                API from anyone outside the owning agency, so rendering it
                whenever present is the entire access rule. */}
            {item.source_url && (
              <div className="flex items-center gap-3 flex-wrap">
                <SourceLinkBadge url={item.source_url} />
                <span className="font-mono text-[11px] text-[var(--mrd-muted)] truncate min-w-0">
                  {item.source_url}
                </span>
              </div>
            )}
            {item.is_editable && (
              <DetailsEditor itemId={item.id} item={item} lang={lang} onSaved={load} />
            )}
            <div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <div className="text-[32px] sm:text-5xl lg:text-6xl font-semibold tracking-tight tabular-nums leading-none text-[var(--mrd-navy)]">
                  {(() => {
                    const d = pickDisplayPrice(item, currency);
                    return formatPrice(d.value, d.currency) || t("price_on_request", lang);
                  })()}
                </div>
                {item.area_sqm && item.price && (
                  <div className="text-sm text-[var(--mrd-muted)]">
                    {(() => {
                      // Per-sqm uses the active currency: USD mode prefers
                      // the pre-computed ``price_per_sqm_usd`` (no FX
                      // round-trip), native falls back to the raw maths.
                      const d = pickDisplayPrice(item, currency);
                      let value: number | null = null;
                      if (currency === "usd" && item.price_per_sqm_usd) {
                        value = Number(item.price_per_sqm_usd);
                      } else if (d.value && item.area_sqm) {
                        value = Number(d.value) / Number(item.area_sqm);
                      }
                      if (value === null || !Number.isFinite(value)) return null;
                      const sym =
                        d.currency === "USD" ? "$" : d.currency === "GEL" ? "₾" : d.currency === "EUR" ? "€" : "";
                      return t("per_sqm", lang, {
                        symbol: sym,
                        value: Math.round(value).toLocaleString(),
                      });
                    })()}
                  </div>
                )}
              </div>
              {item.price_position && (
                <div className="mt-3">
                  <BenchmarkBadge
                    position={item.price_position}
                    percent={item.price_percent_vs_median ?? null}
                    median={item.neighborhood_median_sqm_usd ?? null}
                    sample={item.neighborhood_sample_size ?? null}
                    lang={lang}
                  />
                </div>
              )}
              <h1 className="mshare-serif text-[clamp(26px,4vw,42px)] font-medium text-[var(--mrd-navy)] mt-3 leading-snug max-w-3xl">
                {listingHeadline(item, lang)}
              </h1>
              {(item.neighborhood || item.district) && (
                <div className="text-base text-[var(--mrd-muted)] mt-3 inline-flex items-center gap-1.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span className="text-[var(--mrd-navy)] font-medium">
                    {item.neighborhood || item.district}
                  </span>
                  {item.neighborhood && item.district && (
                    <span className="text-[var(--mrd-muted)]"> · {item.district}</span>
                  )}
                </div>
              )}
            </div>

            {facts.length > 0 && (
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {facts.map((f) => (
                  <div
                    key={f.label}
                    className="rounded-2xl bg-white border border-[var(--mrd-line)] shadow-[0_2px_24px_rgba(18,32,54,.05)] p-4 flex items-center gap-3"
                  >
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[var(--mrd-accent)]/20 text-[var(--mrd-navy)]">
                      {f.icon}
                    </div>
                    <div className="min-w-0">
                      <dt className="font-mono text-[10px] uppercase tracking-[.14em] text-[var(--mrd-muted)]">
                        {f.label}
                      </dt>
                      <dd className="text-base font-semibold text-[var(--mrd-navy)] truncate">
                        {f.value}
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>
            )}

            {(item.lat || item.lon) && (
              <section className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="h-px w-7 bg-[var(--mrd-accent)]" />
                  <h2 className="font-mono text-[11px] uppercase tracking-[.28em] text-[var(--mrd-muted)]">
                    {t("section_location", lang)}
                  </h2>
                </div>
                <LocationMap
                  lat={Number(item.lat)}
                  lon={Number(item.lon)}
                  title={item.title}
                  lang={lang}
                />
              </section>
            )}

            {(item.features.length > 0 || item.amenities.length > 0) && (
              <section className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="h-px w-7 bg-[var(--mrd-accent)]" />
                  <h2 className="font-mono text-[11px] uppercase tracking-[.28em] text-[var(--mrd-muted)]">
                    {t("section_features", lang)}
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[...item.features, ...item.amenities].map((f, i) => (
                    <span
                      key={`${f}-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--mrd-line)] bg-white text-[var(--mrd-navy)] text-xs font-medium px-3 py-1.5"
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--mrd-gold)]">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {featureLabel(f, lang)}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sticky agent CTA — pinned ~12rem below the sticky header. */}
          <aside className="lg:sticky lg:top-20 space-y-4">
            <AgentCard agent={item.agent} lang={lang} variant="sticky" />
            <div className="rounded-2xl border border-[var(--mrd-line)] bg-white shadow-[0_2px_24px_rgba(18,32,54,.05)] p-4 text-xs text-[var(--mrd-muted)] leading-relaxed">
              {t("aside_callout", lang)}
            </div>
          </aside>
        </section>

        {/* ─── Other offers in the same share ─────────────────────── */}
        {otherItems.length > 0 && token && (
          <section className="space-y-4">
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <div className="mb-3 flex items-center gap-3">
                  <span className="h-px w-7 bg-[var(--mrd-accent)]" />
                  <span className="font-mono text-[11px] uppercase tracking-[.28em] text-[var(--mrd-muted)]">
                    {t("section_other_offers", lang)}
                  </span>
                </div>
                <h2 className="mshare-serif text-[clamp(24px,3.4vw,36px)] font-semibold text-[var(--mrd-navy)] leading-[1.1]">
                  {t("section_other_offers_blurb", lang, { count: otherItems.length })}
                </h2>
              </div>
              <Link
                to={`/share/${token}`}
                className="font-mono text-[11px] uppercase tracking-[.14em] text-[var(--mrd-navy)] hover:text-[var(--mrd-navy)]/60 whitespace-nowrap inline-flex items-center gap-1.5 transition-colors shrink-0"
              >
                {t("back_to_selection", lang)}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {otherItems.map((o) => (
                <OtherOfferCard
                  key={o.id}
                  token={token}
                  item={o}
                  lang={lang}
                  currency={currency}
                />
              ))}
            </div>
          </section>
        )}

        {/* ─── Closing CTA strip ──────────────────────────────────── */}
        <section className="rounded-2xl bg-[var(--mrd-navy)] text-white p-8 sm:p-12 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="text-center lg:text-left max-w-xl">
            <h2 className="mshare-serif text-[clamp(26px,4vw,40px)] font-semibold text-white leading-[1.1]">
              {t("cta_headline", lang)}
            </h2>
            <p className="text-sm sm:text-base text-white/65 mt-3 leading-relaxed">{t("cta_blurb", lang)}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-center flex-shrink-0">
            {item.agent.phone && (
              <a
                href={`tel:${item.agent.phone}`}
                className="inline-flex items-center gap-2 rounded-[999px] bg-[var(--mrd-accent)] hover:bg-[var(--mrd-accent-hover)] text-[var(--mrd-ink)] text-[15px] font-semibold px-7 py-4 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
                </svg>
                {t("btn_call_now", lang)}
              </a>
            )}
            {item.agent.phone && (
              <a
                href={`https://wa.me/${item.agent.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(t("whatsapp_listing_msg", lang, { title: titleForMessages }))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-[999px] border border-white/25 bg-white/10 hover:bg-white/20 text-white text-[15px] font-semibold px-7 py-4 transition-colors"
              >
                <WhatsAppIcon className="w-3.5 h-3.5" />
                {t("btn_schedule_whatsapp", lang)}
              </a>
            )}
            {item.agent.email && (
              <a
                href={`mailto:${item.agent.email}?subject=${encodeURIComponent(t("email_subject", lang, { title: titleForMessages }))}`}
                className="inline-flex items-center gap-2 rounded-[999px] border border-white/25 text-white hover:bg-white/10 text-[15px] font-medium px-7 py-4 transition-colors"
              >
                {t("btn_email", lang)}
              </a>
            )}
          </div>
        </section>
      </main>

      {/* Lightbox */}
      {lightboxOpen && cover && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 safe-pt safe-pb safe-px"
          onClick={() => setLightboxOpen(false)}
        >
          <img
            src={cover}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 text-white/80 hover:text-white text-3xl w-10 h-10 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-md"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close"
          >
            ×
          </button>
          {photos.length > 1 && (
            <>
              <button
                type="button"
                className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-white/90 hover:text-white text-3xl sm:text-4xl w-11 h-11 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-md"
                onClick={(e) => {
                  e.stopPropagation();
                  setActivePhoto((i) => (i - 1 + photos.length) % photos.length);
                }}
                aria-label={t("btn_prev", lang)}
              >
                ‹
              </button>
              <button
                type="button"
                className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-white/90 hover:text-white text-3xl sm:text-4xl w-11 h-11 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-md"
                onClick={(e) => {
                  e.stopPropagation();
                  setActivePhoto((i) => (i + 1) % photos.length);
                }}
                aria-label={t("btn_next", lang)}
              >
                ›
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 px-3 py-1 text-[11px] font-semibold text-white tabular-nums">
                {t("counter_x_of_y", lang, { index: activePhoto + 1, total: photos.length })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Sticky mobile CTA. Mirrors the grid view so the buyer can always
          reach the agent without scrolling back to the top bar. */}
      {item.agent.phone && (
        <div className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[var(--mrd-line)] bg-[rgba(255,255,255,.95)] backdrop-blur-md share-mobile-cta-pb pt-3 px-3 safe-px">
          <div className="flex gap-2">
            <a
              href={`tel:${item.agent.phone}`}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-[999px] bg-[var(--mrd-accent)] hover:bg-[var(--mrd-accent-hover)] active:scale-[0.98] text-[var(--mrd-ink)] text-sm font-semibold py-3.5 transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
              </svg>
              {t("btn_call", lang)}
            </a>
            <a
              href={`https://wa.me/${item.agent.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(t("whatsapp_listing_msg", lang, { title: titleForMessages }))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-[999px] border border-[rgba(16,120,80,.3)] bg-[rgba(16,120,80,.08)] active:scale-[0.98] text-[#0f7a50] text-sm font-semibold py-3.5 transition-colors"
            >
              <WhatsAppIcon className="w-3.5 h-3.5" />
              {t("btn_whatsapp", lang)}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── listing-details editor (owner only) ───────────────────────────────────
// Lets the broker who created the link override price + parameters straight
// from the public page. Mirrors DescriptionSection's "you own this link"
// affordance; saving PATCHes the share item and the parent re-fetches so
// derived fields (USD price, benchmark badge) reflect the new values.

const CURRENCY_OPTIONS = ["GEL", "USD", "EUR"] as const;

type DetailsDraft = {
  title: string;
  price: string;
  currency: string;
  deal_type: string;
  property_type: string;
  rooms: string;
  bedrooms: string;
  bathrooms: string;
  area_sqm: string;
  floor: string;
  total_floors: string;
  year_built: string;
  building_status: string;
  condition: string;
  district: string;
  neighborhood: string;
  street: string;
};

function draftFromItem(item: PublicShareItemDetail): DetailsDraft {
  const str = (v: string | null | undefined) => v ?? "";
  const num = (v: number | null | undefined) => (v != null ? String(v) : "");
  return {
    title: str(item.title),
    price: str(item.price),
    currency: str(item.currency) || "GEL",
    deal_type: str(item.deal_type),
    property_type: str(item.property_type),
    rooms: num(item.rooms),
    bedrooms: num(item.bedrooms),
    bathrooms: num(item.bathrooms),
    area_sqm: str(item.area_sqm),
    floor: num(item.floor),
    total_floors: num(item.total_floors),
    year_built: num(item.year_built),
    building_status: str(item.building_status),
    condition: str(item.condition),
    district: str(item.district),
    neighborhood: str(item.neighborhood),
    street: str(item.street),
  };
}

function DetailsEditor({
  itemId,
  item,
  lang,
  onSaved,
}: {
  itemId: string;
  item: PublicShareItemDetail;
  lang: ShareLang;
  onSaved: () => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<DetailsDraft>(() => draftFromItem(item));

  const open = () => {
    setDraft(draftFromItem(item));
    setErr(null);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setErr(null);
  };
  const set = <K extends keyof DetailsDraft>(k: K, v: string) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    setErr(null);
    const intOrNull = (s: string) => {
      const n = parseInt(s.trim(), 10);
      return s.trim() === "" || Number.isNaN(n) ? null : n;
    };
    const payload = {
      title: draft.title.trim(),
      price: draft.price.trim(),
      currency: draft.currency,
      deal_type: draft.deal_type,
      property_type: draft.property_type,
      rooms: intOrNull(draft.rooms),
      bedrooms: intOrNull(draft.bedrooms),
      bathrooms: intOrNull(draft.bathrooms),
      area_sqm: draft.area_sqm.trim(),
      floor: intOrNull(draft.floor),
      total_floors: intOrNull(draft.total_floors),
      year_built: intOrNull(draft.year_built),
      building_status: draft.building_status,
      condition: draft.condition,
      district: draft.district.trim(),
      neighborhood: draft.neighborhood.trim(),
      street: draft.street.trim(),
    };
    try {
      await api(`/api/share-links/items/${itemId}`, { method: "PATCH", json: payload });
      await onSaved();
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t("edit_save_failed", lang));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={open}
        className="inline-flex items-center gap-1.5 rounded-[999px] border border-[var(--mrd-gold)]/40 bg-[var(--mrd-gold)]/[0.08] px-3 py-1.5 text-xs font-semibold text-[var(--mrd-gold)] hover:bg-[var(--mrd-gold)]/[0.16] transition-colors"
        title={t("edit_hint", lang)}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
        {t("edit_details", lang)}
      </button>
    );
  }

  const Text = (key: keyof DetailsDraft, label: string, type = "text") => (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] uppercase tracking-[.14em] text-[var(--mrd-muted)]">
        {label}
      </span>
      <input
        type={type}
        inputMode={type === "number" ? "numeric" : undefined}
        value={draft[key]}
        onChange={(e) => set(key, e.target.value)}
        disabled={saving}
        className="input w-full"
      />
    </label>
  );

  const Select = (
    key: keyof DetailsDraft,
    label: string,
    labels: Record<string, string>,
  ) => (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] uppercase tracking-[.14em] text-[var(--mrd-muted)]">
        {label}
      </span>
      <select
        value={draft[key]}
        onChange={(e) => set(key, e.target.value)}
        disabled={saving}
        className="input w-full"
      >
        <option value="">{t("field_unset", lang)}</option>
        {Object.entries(labels).map(([value, text]) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <section className="rounded-2xl border border-[var(--mrd-gold)]/30 bg-white shadow-[0_2px_24px_rgba(18,32,54,.05)] p-4 sm:p-5 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-mono text-[11px] uppercase tracking-[.28em] text-[var(--mrd-muted)]">
          {t("edit_details_title", lang)}
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">{Text("title", t("field_title", lang))}</div>
        {Text("price", t("field_price", lang), "number")}
        <label className="flex flex-col gap-1">
          <span className="font-mono text-[10px] uppercase tracking-[.14em] text-[var(--mrd-muted)]">
            {t("field_currency", lang)}
          </span>
          <select
            value={draft.currency}
            onChange={(e) => set("currency", e.target.value)}
            disabled={saving}
            className="input w-full"
          >
            {CURRENCY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        {Select("deal_type", t("field_deal_type", lang), DEAL_TYPE_LABEL_BY_LANG[lang])}
        {Select("property_type", t("fact_type", lang), PROPERTY_TYPE_LABEL_BY_LANG[lang])}
        {Text("rooms", t("fact_rooms", lang), "number")}
        {Text("bedrooms", t("fact_bedrooms", lang), "number")}
        {Text("bathrooms", t("fact_bathrooms", lang), "number")}
        {Text("area_sqm", t("fact_area", lang), "number")}
        {Text("floor", t("fact_floor", lang), "number")}
        {Text("total_floors", t("field_total_floors", lang), "number")}
        {Text("year_built", t("fact_year_built", lang), "number")}
        {Select("building_status", t("fact_building", lang), BUILDING_STATUS_LABEL_BY_LANG[lang])}
        {Select("condition", t("fact_condition", lang), CONDITION_LABEL_BY_LANG[lang])}
        {Text("district", t("field_district", lang))}
        {Text("neighborhood", t("field_neighborhood", lang))}
        <div className="sm:col-span-2">{Text("street", t("fact_street", lang))}</div>
      </div>

      {err && <div className="text-xs text-rose-300">{err}</div>}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[11px] text-[var(--mrd-muted)]">{t("edit_hint", lang)}</span>
        <div className="flex items-center gap-2">
          <button type="button" onClick={cancel} disabled={saving} className="btn-secondary text-xs">
            {t("edit_cancel", lang)}
          </button>
          <button type="button" onClick={save} disabled={saving} className="btn-primary text-xs">
            {saving ? t("edit_saving", lang) : t("edit_save", lang)}
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── location map ──────────────────────────────────────────────────────────

const TILE_URL =
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>';

/** Embedded map showing the property's exact location.
 *
 *  Imperative Leaflet (no react-leaflet wrapper) so we keep the bundle lean —
 *  a single pinned marker doesn't need the heavier component library.
 *  Renders nothing when coords are missing/non-numeric.
 */
function LocationMap({
  lat,
  lon,
  title,
  lang,
}: {
  lat: number;
  lon: number;
  title: string | null;
  lang: ShareLang;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    if (mapRef.current) return; // init once

    const map = L.map(containerRef.current, {
      center: [lat, lon],
      zoom: 16,
      zoomControl: true,
      scrollWheelZoom: false, // less hostile on a sales page; users still
      // get pinch-zoom on touch + the +/- buttons.
      minZoom: 12,
      maxZoom: 19,
    });
    L.tileLayer(TILE_URL, {
      attribution: TILE_ATTRIBUTION,
      subdomains: "abcd",
      maxZoom: 19,
      // @ts-expect-error — retina suffix option missing in the types.
      r: window.devicePixelRatio > 1 ? "@2x" : "",
    }).addTo(map);

    // Custom div-icon pin matching the app accent. Default Leaflet PNGs
    // require `<img>` tag fixups for dark backgrounds; a styled div is
    // cleaner and ships zero extra assets.
    const icon = L.divIcon({
      className: "share-map-pin",
      html: `
        <span class="share-map-pin-dot"></span>
        <span class="share-map-pin-pulse"></span>
      `,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    L.marker([lat, lon], { icon }).addTo(map);

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [lat, lon]);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return (
      <div className="rounded-2xl border border-[var(--mrd-line)] bg-white p-4 text-sm text-[var(--mrd-muted)]">
        {t("no_coordinates", lang)}
      </div>
    );
  }
  const gmapsUrl = `https://www.google.com/maps?q=${lat},${lon}`;
  const label = title || t("section_location", lang);
  return (
    <div className="space-y-2">
      <div
        ref={containerRef}
        className="h-72 sm:h-80 lg:h-96 rounded-2xl overflow-hidden border border-[var(--mrd-line)] bg-white"
        aria-label={label}
      />
      <div className="text-right">
        <a
          href={gmapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[.14em] text-[var(--mrd-navy)] hover:text-[var(--mrd-navy)]/60 transition-colors"
        >
          {t("open_in_google_maps", lang)}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17 17 7" />
            <path d="M7 7h10v10" />
          </svg>
        </a>
      </div>
    </div>
  );
}

// ─── "other offers" card ──────────────────────────────────────────────────

/** Compact preview card for sibling offers in the same share. The whole
 *  card is one Link to the other detail page — no preview state, no
 *  "preview" button: clicking *is* the action, since the buyer is already
 *  in detail mode and just wants to flip to the next listing. */
function OtherOfferCard({
  token,
  item,
  lang,
  currency,
}: {
  token: string;
  item: PublicShareItemSummary;
  lang: ShareLang;
  currency: DisplayCurrency;
}) {
  const display = pickDisplayPrice(item, currency);
  const dealLabel = dealTypeLabel(item.deal_type, lang);
  return (
    <Link
      to={`/share/${token}/listings/${item.id}`}
      className="group block rounded-2xl overflow-hidden bg-white border border-[var(--mrd-line)] shadow-[0_2px_24px_rgba(18,32,54,.05)] hover:shadow-[0_12px_40px_rgba(18,32,54,.12)] transition-shadow transition-transform duration-300 hover:-translate-y-1.5"
    >
      <div className="relative aspect-[4/3] bg-[var(--mrd-bg)] overflow-hidden">
        {item.cover_photo_url ? (
          <img
            src={item.cover_photo_url}
            alt={item.title || ""}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--mrd-muted)] text-xs">
            {t("no_photo", lang)}
          </div>
        )}
        {dealLabel && (
          <span className="absolute top-3 left-3 rounded-full bg-white/85 backdrop-blur-md border border-[var(--mrd-line)] px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[.14em] text-[var(--mrd-navy)]">
            {dealLabel}
          </span>
        )}
      </div>
      <div className="p-5">
        {(item.neighborhood || item.district) && (
          <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[var(--mrd-muted)] truncate">
            {item.neighborhood || item.district}
          </div>
        )}
        <div className="mt-2 mshare-serif text-2xl font-medium text-[var(--mrd-navy)] leading-snug transition-colors group-hover:text-[var(--mrd-navy)]/70">
          {listingHeadline(item, lang)}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2 pt-4 border-t border-[var(--mrd-line)] text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-semibold text-[var(--mrd-navy)] tabular-nums whitespace-nowrap">
              {formatPrice(display.value, display.currency) || "—"}
            </span>
            {item.price_position && (
              <BenchmarkBadge
                position={item.price_position}
                percent={item.price_percent_vs_median ?? null}
                lang={lang}
                size="sm"
                className="flex-shrink-0"
              />
            )}
          </div>
          <span className="text-[var(--mrd-muted)] whitespace-nowrap flex-shrink-0">
            {[
              item.rooms != null ? t("rooms_with_count", lang, { count: item.rooms }) : null,
              item.area_sqm ? `${Math.round(Number(item.area_sqm))} m²` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      </div>
    </Link>
  );
}

// ─── tiny icons used in the headline-facts grid ────────────────────────────

function IconRooms() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-6 9 6v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </svg>
  );
}
function IconArea() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 3v18" />
    </svg>
  );
}
function IconFloor() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 2 7l10 5 10-5-10-5z" />
      <path d="M2 12l10 5 10-5" />
      <path d="M2 17l10 5 10-5" />
    </svg>
  );
}
function IconType() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18" />
      <path d="M3 22h18" />
      <path d="M10 6h4M10 10h4M10 14h4" />
    </svg>
  );
}
function IconBuilding() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
    </svg>
  );
}
function IconCondition() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12l2 2 4-4" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}
function IconBedrooms() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4v16" />
      <path d="M2 8h18a2 2 0 0 1 2 2v10" />
      <path d="M2 17h20" />
      <path d="M6 8v9" />
    </svg>
  );
}
function IconBathrooms() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-3 0V11" />
      <path d="M3 11h18v3a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z" />
      <path d="M7 19v3M17 19v3" />
      <path d="M9 7h2" />
    </svg>
  );
}
function IconYear() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}
function IconStreet() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 22 22 2" />
      <path d="m18 22-4-4M10 14 6 10" />
      <path d="M16 16h-4M8 8H4" />
    </svg>
  );
}
